// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  User, Mail, Phone, Lock, Eye, EyeOff, Leaf, AlertCircle,
  CheckCircle, Shield, ArrowLeft
} from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('NAVIROSE');

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) setSiteLogo(data.logo);
          if (data.siteName) setSiteName(data.siteName);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (user) navigate('/profile');
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setError('Saari fields fill karein.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password kam az kam 6 characters ka hona chahiye.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Password aur confirm password match nahi kar rahe.');
      return;
    }

    if (!agreeTerms) {
      setError('Terms & Conditions accept karna zaroori hai.');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, formData.email, formData.password
      );

      const newUser = userCredential.user;
      await updateProfile(newUser, { displayName: formData.name });

      await setDoc(doc(db, 'users', newUser.uid), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: 'customer',
        photoURL: '',
        createdAt: new Date(),
      });

      navigate('/profile');
    } catch (err) {
      console.error('Register error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Ye email pehle se registered hai. Login karein.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Email sahi nahi hai.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password kamzor hai.');
      } else {
        setError('Register nahi ho saka. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    if (!agreeTerms) {
      setError('Pehle Terms & Conditions accept karein.');
      return;
    }
    setGoogleLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;

      const userRef = doc(db, 'users', googleUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          name: googleUser.displayName || 'Customer',
          email: googleUser.email,
          phone: '',
          role: 'customer',
          photoURL: googleUser.photoURL || '',
          createdAt: new Date(),
        });
      }

      navigate('/profile');
    } catch (err) {
      console.error('Google register error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google popup band ho gaya. Dobara try karein.');
      } else {
        setError('Google sign-in nahi ho saka.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-6 md:py-10 anim-fadeIn"
      style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">

        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold btn-2d mb-6"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

          {/* Left Side - Branding */}
          <div className="hidden lg:block anim-fadeInLeft">
            <div className="rounded-3xl p-10 shadow-xl relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #F5AEC0 0%, #075B91 100%)', color: '#fff' }}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white opacity-10 -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white opacity-10 translate-y-1/2 -translate-x-1/2"></div>

              <div className="relative z-10">
                {siteLogo ? (
                  <img src={siteLogo} alt={siteName} className="h-20 w-auto max-w-[200px] object-contain mb-6 rounded" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-6">
                    <Leaf size={32} />
                  </div>
                )}
                <h1 className="text-4xl font-bold mb-4 leading-tight">
                  Join {siteName}<br />Today!
                </h1>
                <p className="text-white/90 mb-8 leading-relaxed">
                  Account banayein aur exclusive discounts, fast checkout, order tracking, aur bohot kuch payein.
                </p>

                <div className="space-y-4">
                  {[
                    'Exclusive Discounts',
                    'Fast Free Shipping',
                    'Order Tracking',
                    'Wishlist & Favorites',
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                        <CheckCircle size={16} />
                      </div>
                      <span className="text-sm font-semibold">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="anim-fadeInRight">
            <div className="rounded-3xl p-6 md:p-8 shadow-xl"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

              {/* Logo/Name */}
              <div className="text-center mb-6">
                {siteLogo ? (
                  <img src={siteLogo} alt={siteName} className="h-16 w-auto max-w-[180px] object-contain mx-auto mb-3" />
                ) : (
                  <div className="inline-flex items-center gap-2 mb-3">
                    <Leaf size={28} style={{ color: 'var(--color-link)' }} />
                    <span className="text-xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
                      {siteName}
                    </span>
                  </div>
                )}
                <h2 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>
                  Create Account
                </h2>
                <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                  Sign up to get started
                </p>
              </div>

              {error && (
                <div className="text-sm p-3 rounded-lg mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 anim-shake">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input type="text" name="name" value={formData.name} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="Ahmed Khan" required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="ahmed@example.com" required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="+92 300 1234567" required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input type={showPassword ? 'text' : 'password'} name="password"
                      value={formData.password} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-11 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="Min 6 characters" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3.5" style={{ color: 'var(--color-secondary-text)' }}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input type={showConfirm ? 'text' : 'password'} name="confirmPassword"
                      value={formData.confirmPassword} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-11 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="Password dobara daalein" required />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-3.5" style={{ color: 'var(--color-secondary-text)' }}>
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-green-700 cursor-pointer" />
                  <span className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>
                    Main <Link to="/terms" className="underline font-semibold" style={{ color: 'var(--color-link)' }}>Terms & Conditions</Link> aur{' '}
                    <Link to="/privacy" className="underline font-semibold" style={{ color: 'var(--color-link)' }}>Privacy Policy</Link> se agree karta hoon.
                  </span>
                </label>

                <button type="submit" disabled={loading}
                  className={`w-full rounded-full py-3.5 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ripple-btn ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                  {loading ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                        style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <Shield size={18} /> Create Account
                    </>
                  )}
                </button>

                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }}></div>
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-secondary-text)' }}>OR</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }}></div>
                </div>

                <button type="button" onClick={handleGoogleRegister} disabled={googleLoading}
                  className={`w-full rounded-full py-3.5 font-semibold flex items-center justify-center gap-3 btn-2d transition ${googleLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
                  {googleLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                      style={{ borderColor: 'var(--color-primary-text)', borderTopColor: 'transparent' }} />
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>

                <p className="text-center text-sm mt-4" style={{ color: 'var(--color-secondary-text)' }}>
                  Pehle se account hai?{' '}
                  <Link to="/login" className="font-semibold underline" style={{ color: 'var(--color-link)' }}>
                    Login karein
                  </Link>
                </p>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;